import { useState, useRef, useEffect } from 'react';
import { Input } from '@/shared/components/ui/input';
import { useCustomers } from '@/shared/hooks/useCustomers';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface CustomerInputProps {
  id?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onCustomerSelect?: (customer: any) => void;
  onCustomerCreate?: (customer: any) => void;
  className?: string;
  required?: boolean;
  field: 'name' | 'phone_number' | 'email';
}

export function CustomerInput({
  id,
  placeholder,
  value,
  onChange,
  onCustomerSelect,
  onCustomerCreate,
  className,
  required,
  field
}: CustomerInputProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { searchCustomers, createCustomer, refetch, loading } = useCustomers();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const hasRefetched = useRef(false);

  const suggestions = searchCustomers(value, field);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(newValue.length >= 2); // Show suggestions after 2+ characters
  };

  const handleSuggestionClick = (customer: any) => {
    setShowSuggestions(false);
    if (onCustomerSelect) {
      onCustomerSelect(customer);
    }
  };

  const handleBlur = async () => {
    // Delay to allow for suggestion clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => {
          if (!hasRefetched.current) {
            hasRefetched.current = true;
            refetch().catch(() => {});
          }
          if (value.length >= 2) setShowSuggestions(true);
        }}
        className={className}
        required={required}
        autoComplete="off"
        spellCheck={false}
        autoCorrect="off"
      />
      
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="absolute z-[200] w-full bg-background border border-border rounded-md shadow-md max-h-48 overflow-y-auto mt-1"
        >
          {loading ? (
            <div className="px-3 py-4 flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-3">
              <div className="text-xs text-muted-foreground">No matching customers</div>
            </div>
          ) : (
            suggestions.map((customer, index) => (
              <div
                key={customer.id}
                className={cn(
                  "px-3 py-2 cursor-pointer hover:bg-accent/50 border-b border-border last:border-b-0",
                  selectedIndex === index && "bg-accent"
                )}
                onClick={() => handleSuggestionClick(customer)}
              >
                <div className="text-sm font-medium">
                  {customer.first_name && customer.last_name
                    ? `${customer.first_name} ${customer.last_name}`
                    : customer.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {customer.phone_number}
                  {customer.email && ` • ${customer.email}`}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
